import { useCallback } from 'react';

import { GetContextMenuItems, GridApi, MenuItemDef } from 'ag-grid-community';

import { exportGridToExcel } from '@/hooks/useExcelExport';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export const DEFAULT_CONTEXT_MENU = [
  'cut',
  'copy',
  'paste',
  'separator',
  'export',
];

export function auditHistoryMenuItem({
  rowId,
}: {
  rowId: string;
}): MenuItemDef {
  return {
    name: 'View History',
    icon: '<i class="pi pi-history" />',
    action() {
      window.open(`/audit/${rowId}`, '_blank');
    },
  };
}

export function buildExportMenuItem(
  api: GridApi,
  autoFilter: boolean,
  opts?: { sheetName?: string; filename?: string; preserveColors?: boolean },
): MenuItemDef {
  return {
    name: 'Export',
    subMenu: [
      {
        name: 'Export to Excel',
        icon: '<span class="ag-icon ag-icon-excel"/>',
        action: () =>
          exportGridToExcel(api, {
            sheetName: opts?.sheetName ?? 'Export',
            filename: opts?.filename ?? 'export.xlsx',
            autoFilter,
            preserveColors: opts?.preserveColors,
          }),
      },
      'csvExport' as const,
    ],
  };
}

export function replaceExportItem(
  items: (string | MenuItemDef)[],
  api: GridApi,
  autoFilter: boolean,
  opts?: { sheetName?: string; filename?: string; preserveColors?: boolean },
): (string | MenuItemDef)[] {
  return items.map((item) =>
    item === 'export' ? buildExportMenuItem(api, autoFilter, opts) : item,
  );
}

export function useContextMenu({ idKey }: { idKey: string }) {
  const autoFilter = useFeatureFlag('Export_To_Excel_Filter_Dropdown');
  const preserveColors = useFeatureFlag('Excel_Keep_Color');

  const getContextMenuItems = useCallback<GetContextMenuItems>(
    (params) => {
      const rowId: string | undefined = idKey && params.node?.data?.[idKey];
      if (!rowId) {
        console.error('Could not determine row ID for context menu');
        return [];
      }

      const items: (string | MenuItemDef)[] = [
        auditHistoryMenuItem({ rowId }),
        'separator',
        ...DEFAULT_CONTEXT_MENU,
      ];

      return replaceExportItem(items, params.api, autoFilter, {
        preserveColors,
      });
    },
    [idKey, autoFilter, preserveColors],
  );

  return { getContextMenuItems };
}
